/**
 * Shared building blocks for the admin metrics page.
 *
 * Phase 0 ships these and four placeholder sections; Phases 1–4 plug in
 * real data by passing an `asyncPanel({ load, render })` per section.
 * Keeping the loading / error / empty-state ceremony here so each phase
 * only writes the aggregate fetch + the HTML render.
 *
 * Styling matches the rest of the admin surface: `.card`, `.muted`,
 * `<h3 class="sec">`, the existing CSS variables in tokens.css.
 */

import { ApiError } from '../api.js';
import { escape } from './form.js';
import { toast } from './toast.js';

/**
 * Render a section wrapper — title + .card body.
 * `children` is an HTML string (matches the rest of the codebase's idiom).
 */
export function section({ title, children = '' }) {
  return `
    <h3 class="sec">${escape(title)}</h3>
    <div class="card" style="padding:14px;">
      ${children}
    </div>
  `;
}

const TONE_VARS = {
  danger: { fg: 'var(--color-danger)', bg: 'var(--color-danger-soft)' },
  ok: { fg: 'var(--color-success)', bg: 'var(--color-success-soft)' },
  muted: { fg: 'var(--color-text-muted)', bg: 'var(--surf-2)' },
};

/**
 * One stat tile.
 *   label  short uppercase caption (e.g. "DAU (24h)")
 *   value  primary number/string already formatted by the caller — we don't
 *          guess; callers pass formatKobo / toLocaleString / etc.
 *   sub    optional secondary line (e.g. "/ 1,240 total")
 *   tone   'danger' | 'ok' | 'muted' | undefined — colours the tile.
 *          Used heavily in Phase 1 for the green/red recon badge.
 */
export function statCard({ label, value, sub = '', tone } = {}) {
  const t = tone && TONE_VARS[tone];
  const bg = t ? t.bg : 'var(--surf-2)';
  const fg = t ? t.fg : '#fff';
  const display = value === null || value === undefined ? '—' : String(value);
  return `
    <div style="text-align:center;padding:10px;background:${bg};border-radius:8px;">
      <div style="font-family:var(--font-display);font-size:22px;font-weight:700;color:${fg};">
        ${escape(display)}
      </div>
      <div class="muted" style="font-size:11px;text-transform:uppercase;letter-spacing:0.06em;margin-top:2px;">
        ${escape(label)}
      </div>
      ${sub ? `<div class="muted" style="font-size:11px;margin-top:4px;">${escape(sub)}</div>` : ''}
    </div>
  `;
}

/**
 * Drive the load -> render lifecycle for a metrics section.
 *
 *   asyncPanel(host, {
 *     load:   () => api('/admin/metrics/financial'),
 *     render: (data) => `<div>...</div>`,
 *     empty:  (data) => boolean,   // optional, defaults to !data
 *     emptyMessage: 'No data yet.',
 *   });
 *
 * Handles the four states each phase would otherwise re-implement: loading
 * skeleton, error (toasted + inlined), empty, and rendered.
 *
 * Calls `navigate('/login')` on 401 to match the rest of the admin pages.
 */
export function asyncPanel(host, { load, render, empty, emptyMessage } = {}) {
  if (!host) return;
  host.innerHTML = `<div class="muted" style="padding:8px 0;">Loading…</div>`;
  Promise.resolve()
    .then(() => load())
    .then((data) => {
      const isEmpty = empty ? empty(data) : data === null || data === undefined;
      if (isEmpty) {
        host.innerHTML = `<div class="muted" style="padding:8px 0;">${escape(emptyMessage || 'Nothing to show yet.')}</div>`;
        return;
      }
      host.innerHTML = render(data);
    })
    .catch(async (err) => {
      if (err instanceof ApiError && err.status === 401) {
        const { navigate } = await import('../router.js');
        navigate('/login');
        return;
      }
      toast.fromError(err);
      const msg = err && err.message ? err.message : 'Could not load metrics.';
      host.innerHTML = `<div style="color:var(--color-danger);padding:8px 0;">${escape(msg)}</div>`;
    });
}
